import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceMutations from "common/datahooks/useWorkspaceMutations";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { WorkspaceNameSchema } from "common/schemas";

import SettingsItemContainer from "../SettingsItemContainer";

export default function WorkspaceName() {
  const isDesktop = useTwBreakpoint("lg");

  const [isEditing, setIsEditing] = useState(false);
  const { id: workspaceId, name, role } = useSelectedWorkspaceContext();
  const canManageWorkspace = role === "owner" || role === "admin";

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string }>({
    resolver: valibotResolver(WorkspaceNameSchema),
    defaultValues: { name },
  });

  const { editWorkspaceName, isEditingWorkspaceName } = useWorkspaceMutations();

  function onSubmit({ name }: { name: string }) {
    editWorkspaceName({ id: workspaceId, name }).then(() => {
      setIsEditing(false);
    });
  }

  return (
    <SettingsItemContainer className="items-center">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <FormField
            label="Workspace name"
            htmlFor="workspace-name"
            className="flex-1"
            size={isDesktop ? "sm" : "lg"}
            error={errors.name?.message}
          >
            <div className="flex flex-col lg:flex-row gap-2">
              <Input
                autoFocus
                variant={isDesktop ? "sm" : "lg"}
                id="workspace-name"
                placeholder="My workspace"
                className="lg:w-80"
                {...register("name")}
              />
              <div className="flex gap-2 ml-auto">
                <Button
                  size={isDesktop ? "sm" : "md"}
                  disabled={isEditingWorkspaceName}
                  variant="secondary-black"
                  onClick={() => {
                    setIsEditing(false);
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size={isDesktop ? "sm" : "md"}
                  isLoading={isEditingWorkspaceName}
                  variant="secondary-purple"
                  type="submit"
                >
                  Save changes
                </Button>
              </div>
            </div>
          </FormField>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-y-0.5">
            <span className="text-sm-bold">Workspace name</span>
            <span className="text-black-500 text-xs-medium">{name}</span>
          </div>
          <RenderIf condition={canManageWorkspace}>
            <Button
              variant="ghost-black"
              intent="iconOnly"
              className="ml-auto"
              onClick={() => setIsEditing(true)}
            >
              <PencilSimpleLine />
            </Button>
          </RenderIf>
        </>
      )}
    </SettingsItemContainer>
  );
}
